// Import react elements
import { useContext, useEffect, useState } from "react";

// Import strich api calls
import {
    checkAffiliateIdAvailability,
    createAffiliateId,
    getAffiliateIds,
    getAffiliateReferrals,
    updateAffiliateDisplayName,
    updateAffiliateId,
} from "api/APIStrichCalls";

// Import context functions
import { AuthContext } from "global/context/AuthContext";
import { UserContext } from "global/context/UserContext";

// Import hooks
import usePageTitle from "global/hooks/usePageTitle";
import { useToast } from "global/hooks/useToast";

// Import components
import { Grid, GridItem } from "global/components/Grid";
import CreateButton from "global/components/CreateButton";
import ToggleSwitch from "global/components/ToggleSwitch";
import CustomisableTable from "global/components/CustomisableTable";
import RadialProgress from "global/components/RadialProgress";
import HelpButton from "global/components/HelpButton";

// Import utilities
import { FormatCurrency } from "global/utilities/ConvertCurrency";
import { formatDisplayDateTime } from "global/utilities/ConvertDate";

// Import css stylesheet
import "./AffiliatesPage.css";



// Format an affiliate decimal rate as a percentage
const formatAffiliateRate = (rate, decimalPlaces = 2) => {

    // Convert the supplied decimal rate into a percentage
    const percentage = Number(rate || 0) * 100;

    // Keep the requested useful decimal places without trailing zeroes
    return percentage.toFixed(decimalPlaces).replace(/\.?0+$/, "") + "%";

};


// Format a currency value as whole units without unnecessary decimal places
const formatWholeCurrency = (currency, amount) => {

    // Drop the minor currency units without rounding the displayed whole amount up
    const wholeMinorAmount = Math.trunc(Number(amount || 0) / 100) * 100;

    // Format the whole-unit amount without a fixed .00 suffix
    return FormatCurrency(currency, wholeMinorAmount, true).replace(/\.00$/, "");

};


// Format a referral status for display
const formatReferralStatus = (status) => {

    // Return a fallback when no status was supplied
    if (!status) { return "Unknown"; }

    // Capitalise the API status
    return status.charAt(0).toUpperCase() + status.slice(1);

};


// Validate an affiliate id before making an availability request
const validateAffiliateId = (affiliate_id) => {

    // Check the affiliate id length
    if (affiliate_id.length < 4) { return "Affiliate ID must be at least 4 characters long."; }
    if (affiliate_id.length > 50) { return "Affiliate ID cannot be more than 50 characters long."; }

    // Check the permitted URL-safe characters
    if (!/^[a-z0-9-]+$/.test(affiliate_id)) { return "Affiliate ID must only use lowercase letters, numbers and hyphens."; }

    // Check the hyphen placement
    if (affiliate_id.startsWith("-") || affiliate_id.endsWith("-")) { return "Affiliate ID cannot start or end with a hyphen."; }
    if (affiliate_id.includes("--")) { return "Affiliate ID cannot contain repeated hyphens."; }

    // The affiliate id is valid locally
    return "";

};

// Format the columns for the referred accounts table
const referralTableColumns = [

    { key: "account", label: "Account", resizable: true, minWidth: 90, maxWidth: 180, draggable: true, textFilter: true },

    { key: "affiliateId", label: "Affiliate ID", resizable: true, minWidth: 110, maxWidth: 240, draggable: true, textFilter: true },

    { key: "registered", label: "Registered", resizable: true, minWidth: 120, maxWidth: 180, draggable: true, textFilter: true, sortKey: "registeredSort" },

    { key: "referralEnds", label: "Referral Ends", resizable: true, minWidth: 120, maxWidth: 180, draggable: true, textFilter: true, sortKey: "referralEndsSort" },

    { key: "statusDisplay", label: "Status", resizable: true, minWidth: 90, maxWidth: 160, draggable: true, textFilter: true, sortKey: "status", filterKey: "status" },

    { key: "rollingValue", label: "30 Day Value", resizable: true, minWidth: 110, maxWidth: 180, draggable: true, textFilter: true, sortKey: "rollingValueSort", td_classname: "alight-to-right" },

    { key: "appliedRate", label: "Applied Rate", resizable: true, minWidth: 100, maxWidth: 150, draggable: true, textFilter: true, sortKey: "appliedRateSort", td_classname: "alight-to-right" },

    { key: "earnings", label: "30 Day Earnings", resizable: true, minWidth: 110, maxWidth: 180, draggable: true, textFilter: true, sortKey: "earningsSort", td_classname: "alight-to-right" },

];

const AffiliatesPage = () => {

    // Set the page title
    usePageTitle("Affiliates");

    // Get the authentication token
    const { token } = useContext(AuthContext);

    // Get the account data and setter
    const { userData, setUserData } = useContext(UserContext);

    // Get toast helpers
    const { toastSuccess, toastError } = useToast();

    // Store the editable public affiliate name
    const [displayName, setDisplayName] = useState(userData?.affiliate_display_name || "");

    // Track the display name save state
    const [savingDisplayName, setSavingDisplayName] = useState(false);

    // Store the account affiliate ids
    const [affiliateIds, setAffiliateIds] = useState([]);

    // Store editable private labels separately from the saved records
    const [affiliateNames, setAffiliateNames] = useState({});

    // Track the affiliate id loading state
    const [loadingAffiliateIds, setLoadingAffiliateIds] = useState(true);

    // Store the new affiliate id form
    const [newAffiliateId, setNewAffiliateId] = useState("");
    const [newAffiliateName, setNewAffiliateName] = useState("");

    // Store the live affiliate id availability state
    const [affiliateIdAvailable, setAffiliateIdAvailable] = useState(false);
    const [affiliateIdMessage, setAffiliateIdMessage] = useState("");
    const [checkingAffiliateId, setCheckingAffiliateId] = useState(false);

    // Track affiliate id creation
    const [creatingAffiliateId, setCreatingAffiliateId] = useState(false);

    // Track the affiliate id currently being updated
    const [updatingAffiliateId, setUpdatingAffiliateId] = useState("");

    // Store the affiliate referral summary and referred accounts
    const [referralData, setReferralData] = useState({ summary: null, accounts: [] });

    // Track referral reporting loading
    const [loadingReferrals, setLoadingReferrals] = useState(true);

    // Store the referred accounts table filters
    const [referralTableFilters] = useState({});

    // Store selected referred account rows for the shared table component
    const [selectedReferralRows, setSelectedReferralRows] = useState([]);

    // Store the selected referred-account ordering preset
    const [referralSortMode, setReferralSortMode] = useState("status");



    // Get the currently saved public affiliate name
    const savedDisplayName = String(userData?.affiliate_display_name || "").trim();

    // Check whether the account has completed its public affiliate identity
    const hasSavedDisplayName = savedDisplayName !== "";

    // Check whether the public display name field has unsaved changes
    const displayNameChanged = displayName.trim() !== savedDisplayName;

    // Get the current referral summary and account list
    const referralSummary = referralData?.summary || {};
    const referredAccounts = Array.isArray(referralData?.accounts) ? referralData.accounts : [];

    // Format the referred accounts for the shared table component
    const referralTableData = referredAccounts.map((account) => {

        // Get the recent earning currency and amount
        const earningsCurrency = account?.affiliate_earnings_30_days?.currency || userData?.currency;
        const earningsAmount = Number(account?.affiliate_earnings_30_days?.amount || 0);

        // Build the referred account table row
        return {
            id: account.marketplace_id || `${account.affiliate_id}-${account.registered_date}`,
            account: account.marketplace_id || "—",
            affiliateId: account.affiliate_id || "—",

            registered: formatDisplayDateTime(account.registered_date, false) || "—",
            registeredSort: account.registered_date || "",

            referralEnds: formatDisplayDateTime(account.referral_end, false) || "—",
            referralEndsSort: account.referral_end || "",

            status: account.status || "",
            statusDisplay: (
                <span className={`affiliate-page__status affiliate-page__status--${account.status || "unknown"}`}>
                    {formatReferralStatus(account.status)}
                </span>
            ),

            rollingValue: (account.rolling_commissionable_currency || userData?.currency)
                ? FormatCurrency(account.rolling_commissionable_currency || userData?.currency, account.rolling_commissionable_amount || 0, true)
                : "—",
            rollingValueSort: Number(account.rolling_commissionable_amount || 0),

            appliedRate: formatAffiliateRate(account.applied_rate, 2),
            appliedRateSort: Number(account.applied_rate || 0),

            earnings: earningsCurrency ? FormatCurrency(earningsCurrency, earningsAmount, true) : "—",
            earningsSort: earningsAmount,
        };

    });

    // Sort referred accounts using the selected quick ordering preset
    const sortedReferralTableData = [...referralTableData].sort((a, b) => {

        // Sort by recent affiliate earnings when requested
        if (referralSortMode === "earnings") {
            const earningsDifference = b.earningsSort - a.earningsSort;
            if (earningsDifference !== 0) { return earningsDifference; }
            return String(b.registeredSort).localeCompare(String(a.registeredSort));
        }

        // Sort newest registrations first when requested
        if (referralSortMode === "recent") {
            return String(b.registeredSort).localeCompare(String(a.registeredSort));
        }

        // Rank current referral states with active accounts first
        const statusOrder = {
            active: 0,
            dormant: 1,
            expired: 2,
            deactivated: 3,
        };

        let aStatusOrder = 99;
        let bStatusOrder = 99;

        if (Object.prototype.hasOwnProperty.call(statusOrder, a.status)) { aStatusOrder = statusOrder[a.status]; }
        if (Object.prototype.hasOwnProperty.call(statusOrder, b.status)) { bStatusOrder = statusOrder[b.status]; }

        const statusDifference = aStatusOrder - bStatusOrder;
        if (statusDifference !== 0) { return statusDifference; }

        // Within each state, show the highest recent earners first
        const earningsDifference = b.earningsSort - a.earningsSort;
        if (earningsDifference !== 0) { return earningsDifference; }

        // Use newest registration as the final stable ordering
        return String(b.registeredSort).localeCompare(String(a.registeredSort));

    });

    // Get the recent and lifetime affiliate earning totals
    const affiliateEarnings30Days = Number(referralSummary?.affiliate_earnings_30_days || 0);
    const affiliateEarningsLifetime = Number(referralSummary?.affiliate_earnings_lifetime || 0);
    const affiliateEarningsCurrency = referralSummary?.affiliate_earnings_currency || userData?.currency;

    // Calculate progress towards the current affiliate rate target
    const affiliateRateTarget = Number(referralSummary?.rate_target_amount || 0);
    const affiliateRollingValue = Number(referralSummary?.rolling_commissionable_amount || 0);
    const affiliateTargetProgress = affiliateRateTarget > 0
        ? Math.max(0, Math.min(100, (affiliateRollingValue / affiliateRateTarget) * 100))
        : 0;



    // Keep the editable display name in sync with refreshed account data
    useEffect(() => {

        // Update the field from the saved account data
        setDisplayName(userData?.affiliate_display_name || "");

    }, [userData?.affiliate_display_name]);



    // Store returned affiliate ids and initialise their editable private labels
    const applyAffiliateIds = (results) => {

        // Normalise the returned affiliate ids
        const rows = Array.isArray(results) ? results : [];

        // Store the affiliate ids
        setAffiliateIds(rows);

        // Build the editable private labels
        const names = {};

        // Add each affiliate id label
        rows.forEach((affiliate) => {
            names[affiliate.affiliate_id] = affiliate.name || "";
        });

        // Store the editable labels
        setAffiliateNames(names);

    };



    // Load this account's affiliate ids
    useEffect(() => {

        // Stop until authentication is available
        if (!token) { return; }

        // Load the affiliate ids
        const loadAffiliateIds = async () => {

            // Show the loading state
            setLoadingAffiliateIds(true);

            // Request the affiliate ids
            const response = await getAffiliateIds(token);

            // Store the returned affiliate ids
            if (response?.success) {

                applyAffiliateIds(response.results);

            } else {

                toastError({
                    title: "Error",
                    message: response?.error_message || "Unable to load affiliate links.",
                });

            }

            // Clear the loading state
            setLoadingAffiliateIds(false);

        };

        // Load the affiliate ids
        loadAffiliateIds();

        // Only reload the affiliate ids when the authentication token changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);



    // Load the referral summary and referred accounts
    useEffect(() => {

        // Stop until authentication is available
        if (!token) { return; }

        // Load the affiliate referral reporting
        const loadAffiliateReferrals = async () => {

            // Show the loading state
            setLoadingReferrals(true);

            // Request the referral summary and accounts
            const response = await getAffiliateReferrals(token);

            // Store the returned referral reporting
            if (response?.success && response?.results) {

                setReferralData({
                    summary: response.results.summary || null,
                    accounts: Array.isArray(response.results.accounts) ? response.results.accounts : [],
                });

            } else {

                setReferralData({ summary: null, accounts: [] });

                toastError({
                    title: "Error",
                    message: response?.error_message || "Unable to load affiliate referrals.",
                });

            }

            // Clear the loading state
            setLoadingReferrals(false);

        };

        // Load the affiliate referral reporting
        loadAffiliateReferrals();

        // Only reload referral reporting when the authentication token changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);



    // Check new affiliate ids after the user pauses typing
    useEffect(() => {

        // Normalise the entered affiliate id
        const affiliate_id = newAffiliateId.trim().toLowerCase();

        // Clear availability when the field is empty
        if (affiliate_id === "") {
            setAffiliateIdAvailable(false);
            setAffiliateIdMessage("");
            setCheckingAffiliateId(false);
            return;
        }

        // Run the local validation first
        const validation_error = validateAffiliateId(affiliate_id);

        // Show local validation errors without making an API call
        if (validation_error) {
            setAffiliateIdAvailable(false);
            setAffiliateIdMessage(validation_error);
            setCheckingAffiliateId(false);
            return;
        }

        // Track whether this availability check has been replaced
        let cancelled = false;

        // Wait briefly before making the API request
        const timer = window.setTimeout(async () => {

            // Show the checking state
            setCheckingAffiliateId(true);
            setAffiliateIdAvailable(false);
            setAffiliateIdMessage("Checking availability...");

            // Check the affiliate id
            const response = await checkAffiliateIdAvailability(token, affiliate_id);

            // Stop when a newer check has replaced this one
            if (cancelled) { return; }

            // Show an available affiliate id
            if (response?.success) {

                setAffiliateIdAvailable(true);
                setAffiliateIdMessage("This affiliate ID is available.");

            } else {

                setAffiliateIdAvailable(false);
                setAffiliateIdMessage(response?.error_message || "This affiliate ID is not available.");

            }

            // Clear the checking state
            setCheckingAffiliateId(false);

        }, 400);

        // Cancel the previous availability check
        return () => {
            cancelled = true;
            window.clearTimeout(timer);
        };

    }, [newAffiliateId, token]);



    // Save the public affiliate display name
    const handleSaveDisplayName = async () => {

        // Normalise the entered display name
        const affiliate_display_name = displayName.trim();

        // Validate the display name
        if (affiliate_display_name.length < 2) {
            toastError({ title: "Error", message: "Affiliate display name must be at least 2 characters long." });
            return;
        }

        if (affiliate_display_name.length > 100) {
            toastError({ title: "Error", message: "Affiliate display name cannot be more than 100 characters long." });
            return;
        }

        // Stop when there is nothing to save
        if (affiliate_display_name === savedDisplayName) { return; }

        // Show the saving state
        setSavingDisplayName(true);

        // Update the normal account record
        const response = await updateAffiliateDisplayName(token, affiliate_display_name);

        // Update the local account data when saved
        if (response?.success) {

            setUserData((previousUserData) => ({
                ...previousUserData,
                affiliate_display_name: affiliate_display_name,
            }));

            toastSuccess({
                title: "Success",
                message: "Affiliate display name updated.",
            });

        } else {

            toastError({
                title: "Error",
                message: response?.error_message || "Unable to update affiliate display name.",
            });

        }

        // Clear the saving state
        setSavingDisplayName(false);

    };



    // Create a new affiliate id
    const handleCreateAffiliateId = async () => {

        // Stop when the public identity has not been saved
        if (!hasSavedDisplayName || displayNameChanged) {
            toastError({ title: "Error", message: "Save your affiliate display name before creating an affiliate link." });
            return;
        }

        // Stop when the affiliate id has not passed availability checking
        if (!affiliateIdAvailable) {
            toastError({ title: "Error", message: affiliateIdMessage || "Please choose an available affiliate ID." });
            return;
        }

        // Validate the optional private label
        if (newAffiliateName.trim().length > 100) {
            toastError({ title: "Error", message: "Private label cannot be more than 100 characters long." });
            return;
        }

        // Show the creation state
        setCreatingAffiliateId(true);

        // Create the affiliate id
        const response = await createAffiliateId(
            token,
            newAffiliateId.trim().toLowerCase(),
            newAffiliateName.trim()
        );

        // Add the new affiliate id when creation succeeds
        if (response?.success && response?.results) {

            const updatedAffiliateIds = [
                ...affiliateIds,
                response.results,
            ];

            applyAffiliateIds(updatedAffiliateIds);

            // Reset the creation form
            setNewAffiliateId("");
            setNewAffiliateName("");
            setAffiliateIdAvailable(false);
            setAffiliateIdMessage("");

            toastSuccess({
                title: "Success",
                message: "Affiliate link created.",
            });

        } else {

            toastError({
                title: "Error",
                message: response?.error_message || "Unable to create affiliate link.",
            });

        }

        // Clear the creation state
        setCreatingAffiliateId(false);

    };



    // Update one editable private label
    const handleAffiliateNameChange = (affiliate_id, value) => {

        // Store the new label against this affiliate id
        setAffiliateNames((previousNames) => ({
            ...previousNames,
            [affiliate_id]: value,
        }));

    };



    // Save an affiliate id private label
    const handleSaveAffiliateName = async (affiliate) => {

        // Get the edited label
        const name = String(affiliateNames[affiliate.affiliate_id] || "").trim();

        // Validate the private label
        if (name.length > 100) {
            toastError({ title: "Error", message: "Private label cannot be more than 100 characters long." });
            return;
        }

        // Mark this affiliate id as updating
        setUpdatingAffiliateId(affiliate.affiliate_id);

        // Save the private label
        const response = await updateAffiliateId(
            token,
            affiliate.affiliate_id,
            { name: name }
        );

        // Refresh the affiliate ids when saved
        if (response?.success) {

            applyAffiliateIds(response.results);

            toastSuccess({
                title: "Success",
                message: "Affiliate link updated.",
            });

        } else {

            toastError({
                title: "Error",
                message: response?.error_message || "Unable to update affiliate link.",
            });

        }

        // Clear the updating state
        setUpdatingAffiliateId("");

    };



    // Enable or disable an affiliate id
    const handleAffiliateActiveChange = async (affiliate, active) => {

        // Mark this affiliate id as updating
        setUpdatingAffiliateId(affiliate.affiliate_id);

        // Update the active state
        const response = await updateAffiliateId(
            token,
            affiliate.affiliate_id,
            { active: active ? "Y" : "N" }
        );

        // Refresh the affiliate ids when updated
        if (response?.success) {

            applyAffiliateIds(response.results);

            toastSuccess({
                title: "Success",
                message: active ? "Affiliate link enabled." : "Affiliate link disabled.",
            });

        } else {

            toastError({
                title: "Error",
                message: response?.error_message || "Unable to update affiliate link.",
            });

        }

        // Clear the updating state
        setUpdatingAffiliateId("");

    };



    // Copy an affiliate link to the clipboard
    const handleCopyAffiliateLink = async (affiliate_id) => {

        // Build the full referral link for the current environment
        const affiliate_url = window.location.origin + "/landing?ref=" + encodeURIComponent(affiliate_id);

        try {

            // Copy the affiliate link
            await navigator.clipboard.writeText(affiliate_url);

            toastSuccess({
                title: "Copied",
                message: "Affiliate link copied to clipboard.",
            });

        } catch (error) {

            // Log clipboard failures
            console.error("Copy affiliate link error:", error);

            toastError({
                title: "Error",
                message: "Unable to copy the affiliate link.",
            });

        }

    };



    return (

        <div className="affiliate-page">

            <div className="affiliate-page__section-heading affiliate-page__section-heading--first">

            </div>

            {loadingReferrals ?

                <div className="affiliate-page__report-loading">
                    Loading affiliate performance...
                </div>

            :

                <Grid className="affiliate-page__summary-grid">

                    <GridItem className="affiliate-page__summary-card">

                        <p className="affiliate-page__summary-label">Current Rate <HelpButton section="affiliates" code="commission-rates" label="Commission Rates" /></p>

                        <p className="affiliate-page__summary-value">
                            {formatAffiliateRate(referralSummary.nominal_rate, 2)}
                        </p>

                        <p className="affiliate-page__summary-meta">
                            Continuous rate based on qualifying referred business over the last {Number(referralSummary.commissionable_days || 30).toLocaleString("en-GB")} days
                        </p>

                    </GridItem>

                    <GridItem className="affiliate-page__summary-card">

                        <p className="affiliate-page__summary-label">30 Day Qualifying Value</p>

                        <div className="affiliate-page__progress-summary">

                            <div className="affiliate-page__progress-ring">

                                <RadialProgress
                                    value={affiliateTargetProgress}
                                    direction="clockwise"
                                    size={120}
                                    strokeWidth={10}
                                    progressColor="var(--primary-color)"
                                    bgColor="var(--input-border)"
                                />

                                <span className="affiliate-page__progress-text">
                                    {affiliateTargetProgress > 0 && affiliateTargetProgress < 1 ? "<1%" : `${Math.round(affiliateTargetProgress)}%`}
                                </span>

                            </div>

                            <div className="affiliate-page__progress-value">

                                <p className="affiliate-page__summary-value affiliate-page__summary-value--progress">
                                    {referralSummary.rate_currency
                                        ? formatWholeCurrency(referralSummary.rate_currency, referralSummary.rolling_commissionable_amount || 0)
                                        : "—"}
                                </p>

                                <p className="affiliate-page__progress-target">
                                    {referralSummary.rate_currency
                                        ? `of ${formatWholeCurrency(referralSummary.rate_currency, referralSummary.rate_target_amount || 0)}`
                                        : "Current rolling commissionable value"}
                                </p>

                            </div>

                        </div>

                    </GridItem>

                    <GridItem className="affiliate-page__summary-card">

                        <p className="affiliate-page__summary-label">Referred Accounts</p>

                        <p className="affiliate-page__summary-value">
                            {Number(referralSummary.referred_accounts || 0).toLocaleString("en-GB")}
                        </p>

                        <p className="affiliate-page__summary-meta">

                            <span className="affiliate-page__summary-line">
                                {Number(referralSummary.contributing_accounts || 0).toLocaleString("en-GB")} contributing
                            </span>

                            <span className="affiliate-page__summary-line">
                                {Number(referralSummary.dormant_accounts || 0).toLocaleString("en-GB")} dormant
                            </span>

                            <span className="affiliate-page__summary-line">
                                {Number(referralSummary.expired_accounts || 0).toLocaleString("en-GB")} expired
                            </span>

                            <span className="affiliate-page__summary-line">
                                {Number(referralSummary.deactivated_accounts || 0).toLocaleString("en-GB")} deactivated
                            </span>

                        </p>

                    </GridItem>

                    <GridItem className="affiliate-page__summary-card">

                        <p className="affiliate-page__summary-label">Affiliate Earnings (30 days)</p>

                        <p className="affiliate-page__summary-value">
                            {affiliateEarningsCurrency ? FormatCurrency(affiliateEarningsCurrency, affiliateEarnings30Days, true) : "—"}
                        </p>

                        <p className="affiliate-page__summary-meta">
                            Lifetime {affiliateEarningsCurrency ? FormatCurrency(affiliateEarningsCurrency, affiliateEarningsLifetime, true) : "—"}
                        </p>

                    </GridItem>

                </Grid>

            }

            <Grid>

                <GridItem className="affiliate-page__public-name-card">

                    <div className="affiliate-page__panel-title">

                        <h3>Public Affiliate Name</h3>

                        <HelpButton section="affiliates" code="affiliate-links" label="Public Affiliate Name" />

                    </div>

                    <div className="affiliate-page__public-name-row">

                        <input
                            className="affiliate-page__input"
                            type="text"
                            maxLength={100}
                            value={displayName}
                            onChange={(event) => setDisplayName(event.target.value)}
                            placeholder="Your name or business name"
                        />

                        <CreateButton
                            title={savingDisplayName ? "Saving..." : "Save"}
                            size="s"
                            onClick={handleSaveDisplayName}
                            disabled={
                                savingDisplayName
                                || displayName.trim().length < 2
                                || !displayNameChanged
                            }
                        />

                    </div>

                </GridItem>

                <GridItem className="affiliate-page__links-card">

                    <div className="affiliate-page__panel-title">

                        <h3>Affiliate Links</h3>

                        <HelpButton section="affiliates" code="affiliate-links" label="Affiliate Links" />

                    </div>

                    {!hasSavedDisplayName &&

                        <p className="affiliate-page__validation affiliate-page__validation--error">
                            Save your public affiliate name before creating an affiliate link.
                        </p>

                    }

                    <div className="affiliate-page__new-link-row">

                        <label className="affiliate-page__inline-field">

                            <span>Affiliate ID</span>

                            <input
                                className="affiliate-page__input"
                                type="text"
                                maxLength={50}
                                value={newAffiliateId}
                                onChange={(event) => setNewAffiliateId(event.target.value.toLowerCase())}
                                placeholder="my-youtube-link"
                                disabled={!hasSavedDisplayName}
                            />

                        </label>

                        <label className="affiliate-page__inline-field">

                            <span>Private label</span>

                            <input
                                className="affiliate-page__input"
                                type="text"
                                maxLength={100}
                                value={newAffiliateName}
                                onChange={(event) => setNewAffiliateName(event.target.value)}
                                placeholder="YouTube"
                                disabled={!hasSavedDisplayName}
                            />

                        </label>

                        <CreateButton
                            title={creatingAffiliateId ? "Adding..." : "Add New"}
                            size="s"
                            className="affiliate-page__add-button"
                            onClick={handleCreateAffiliateId}
                            disabled={
                                creatingAffiliateId
                                || checkingAffiliateId
                                || !hasSavedDisplayName
                                || displayNameChanged
                                || !affiliateIdAvailable
                            }
                        />

                    </div>

                    {affiliateIdMessage &&

                        <p className={`affiliate-page__validation ${affiliateIdAvailable ? "affiliate-page__validation--success" : "affiliate-page__validation--error"}`}>
                            {affiliateIdMessage}
                        </p>

                    }

                    {loadingAffiliateIds ?

                        <p>Loading affiliate links...</p>

                    :

                        affiliateIds.length === 0 ?

                            <p className="affiliate-page__help">
                                You haven't created any affiliate links yet.
                            </p>

                        :

                            <div className="affiliate-page__links">

                                {affiliateIds.map((affiliate) => {

                                    // Build the full referral URL
                                    const affiliate_url = window.location.origin + "/landing?ref=" + encodeURIComponent(affiliate.affiliate_id);

                                    // Check whether this row is currently being updated
                                    const isUpdating = updatingAffiliateId === affiliate.affiliate_id;

                                    // Get the currently edited private label
                                    const editedName = affiliateNames[affiliate.affiliate_id] || "";

                                    // Check whether the private label has changed
                                    const nameChanged = editedName.trim() !== String(affiliate.name || "").trim();

                                    return (

                                        <div
                                            key={affiliate.affiliate_id}
                                            className="affiliate-page__link"
                                        >

                                            <div className="affiliate-page__link-identity">

                                                <strong>{affiliate.affiliate_id}</strong>

                                                <span className="affiliate-page__link-url">
                                                    {affiliate_url}
                                                </span>

                                            </div>

                                            <CreateButton
                                                title="Copy Link"
                                                size="s"
                                                invert={true}
                                                onClick={() => handleCopyAffiliateLink(affiliate.affiliate_id)}
                                            />

                                            <input
                                                className="affiliate-page__input"
                                                type="text"
                                                maxLength={100}
                                                value={editedName}
                                                onChange={(event) => handleAffiliateNameChange(affiliate.affiliate_id, event.target.value)}
                                                placeholder="Private label"
                                                disabled={isUpdating}
                                            />

                                            <CreateButton
                                                title="Save"
                                                size="s"
                                                onClick={() => handleSaveAffiliateName(affiliate)}
                                                disabled={!nameChanged || isUpdating}
                                            />

                                            <ToggleSwitch
                                                name={`affiliate_${affiliate.affiliate_id}`}
                                                value={affiliate.active === "Y"}
                                                label={affiliate.active === "Y" ? "Active" : "Inactive"}
                                                labelPosition="left"
                                                size="s"
                                                disabled={isUpdating}
                                                onChange={(event) => handleAffiliateActiveChange(affiliate, event.target.value)}
                                            />

                                        </div>

                                    );

                                })}

                            </div>

                    }

                </GridItem>

                <GridItem className="affiliate-page__referrals-card">

                    <div className="affiliate-page__referrals-header">

                        <h3>Referred Accounts <HelpButton section="affiliates" code="referred-accounts" label="Referred Accounts" /></h3>

                        <div className="affiliate-page__sort-presets">

                            <button
                                type="button"
                                className={`affiliate-page__sort-button ${referralSortMode === "status" ? "affiliate-page__sort-button--active" : ""}`}
                                onClick={() => setReferralSortMode("status")}
                            >
                                Active first
                            </button>

                            <button
                                type="button"
                                className={`affiliate-page__sort-button ${referralSortMode === "earnings" ? "affiliate-page__sort-button--active" : ""}`}
                                onClick={() => setReferralSortMode("earnings")}
                            >
                                Biggest earners
                            </button>

                            <button
                                type="button"
                                className={`affiliate-page__sort-button ${referralSortMode === "recent" ? "affiliate-page__sort-button--active" : ""}`}
                                onClick={() => setReferralSortMode("recent")}
                            >
                                Most recent
                            </button>

                        </div>

                    </div>

                    <div className="table__full-width-scroll">

                        <CustomisableTable
                            key={`affiliate-referral-${referralSortMode}`}
                            tableData={sortedReferralTableData}
                            initialTableColumns={referralTableColumns}
                            filterKeys={[]}
                            filters={referralTableFilters}
                            selectedRows={selectedReferralRows}
                            setSelectedRows={setSelectedReferralRows}
                            loadingData={loadingReferrals}
                            noDataAvaiableMessage="No accounts have registered through your affiliate links yet."
                            rowKeyPrefix="affiliate-referral"
                        />

                    </div>

                </GridItem>

            </Grid>

        </div>

    );

};



export default AffiliatesPage;